import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["accessLevel", "actions", "objectId", "objectTypeId", "property", "value", "formMetadata", "enableConditionalPropertyOptions", "onFocus", "onBlur", "formControlProps", "getAdditionalPropertyValue", "getSavedValue", "getDraftValue"];
import { useMemo, useRef, useState } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import { HIDDEN } from 'customer-data-objects/property/AccessLevel';
import { isFile } from 'customer-data-objects/property/PropertyIdentifier';
import shouldShowAIComponentForStatus from '../../ai/shouldShowAIComponentForStatus';
import { useSavingMode } from '../../autosave/hooks/useSavingMode';
import useComponentMountMetric from '../../metrics/useComponentMountMetric';
import { usePropertyInputCanaryMetrics } from '../../metrics/usePropertyInputCanaryMetrics';
import { ActionsContext } from '../context/ActionsContext';
import { FocusProvider, useFocusContext } from '../context/FocusContext';
import { IdentifierProvider } from '../context/IdentifierContext';
import { PropertyContextProvider } from '../context/PropertyContext/PropertyContextProvider';
import { useConditionalPropertyOptions } from '../hooks/useConditionalPropertyOptions';
import { useGetAdditionalPropertyValue } from '../hooks/useGetAdditionalPropertyValue';
import { useGetIsUngatedForConditionalAssociations } from '../hooks/useGetIsUngatedForConditionalAssociations';
import { useIsTieredPricingProperty } from '../hooks/useIsTieredPricingProperty';
import { useObjectId } from '../hooks/useObjectId';
import { useObjectTypeId } from '../hooks/useObjectTypeId';
import { useProperty } from '../hooks/useProperty';
import { usePropertyAccessLevel } from '../hooks/usePropertyAccessLevel';
import { usePropertyInputComponent } from '../hooks/usePropertyInputComponent';
import { usePropertyInputFeatures } from '../hooks/usePropertyInputFeatures';
import { usePropertyInputNormalization } from '../hooks/usePropertyInputNormalization';
import { usePropertyValue } from '../hooks/usePropertyValue';
import { EDIT_INPUT_MODE } from '../types/PropertyInputV2Component';
import PropertyInputFormControl from './PropertyInputFormControl';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
const PropertyInputV2Wrapped = props => {
  const {
    autoFocus,
    disabled = false,
    enableAutosave = false,
    disableValidation = false,
    enableMultipleEmailInput = false,
    enablePopovers = true,
    enableCheckboxInput = false,
    enablePhoneNumberInput = true,
    enableConditionalPropertiesPanel = false,
    enableDependentAssociations = false,
    conditionalPropertyRules = undefined,
    FormControl: FormControlComponent,
    formControlProps = {},
    inputMode = EDIT_INPUT_MODE,
    inputProps = {},
    inputRef: inputRefProp,
    onDetailsClick,
    onChange,
    onKeyUp,
    onSave = emptyFunction,
    onTracking,
    onValidationChange = emptyFunction,
    onOpenChange = emptyFunction,
    popoverProps,
    readOnly = false,
    use = 'default',
    validationSettings = {},
    showAutoSaveText,
    htmlInputOverride
  } = props;
  const accessLevel = usePropertyAccessLevel();
  const property = useProperty();
  const propertyValue = usePropertyValue();
  const objectId = useObjectId();
  const objectTypeId = useObjectTypeId();
  const getAdditionalPropertyValue = useGetAdditionalPropertyValue();
  const {
    onFocus,
    onBlur
  } = useFocusContext();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const inputRef = useRef(null);
  const {
    data: savingMode
  } = useSavingMode();
  const hasDependentAssociationsAccess = useGetIsUngatedForConditionalAssociations({
    enableDependentAssociations
  });

  // A small percentage of users have access to a setting to turn off autosave in favor of manual saving.
  // In this case, the PropertyInputV2 component still manages property saving, but the user will have a manual save
  // UX which should not use the autosave copy (which shows "Save" for some action buttons)
  const isReallyAutosaving = savingMode === 'auto' && enableAutosave;
  const showAutoSaveTextInternal = isReallyAutosaving || showAutoSaveText;
  const {
    aiProcessingStatus,
    conditionalPropertiesPanel,
    isHidingHighlySensitiveValue,
    isPropertyReadOnly,
    onChangeMultiple,
    onChangeSingle,
    shouldValidate,
    validationState,
    wrappedOnBlur,
    wrappedOnFocus,
    wrappedOnOpenChange,
    wrappedOnValidationChange,
    deactivateProductModal
  } = usePropertyInputFeatures({
    accessLevel,
    conditionalPropertyRules,
    disabled,
    disableValidation,
    enableAutosave,
    enableConditionalPropertiesPanel,
    enableDependentAssociations: hasDependentAssociationsAccess,
    getAdditionalPropertyValue,
    inputMode,
    objectId,
    objectTypeId,
    onBlur,
    onChange,
    onFocus,
    onOpenChange,
    onSave,
    onValidationChange,
    property,
    propertyValue,
    readOnly,
    showAutoSaveText: showAutoSaveTextInternal,
    use,
    validationSettings
  });
  const {
    invalid,
    message,
    additionalHelp
  } = validationState;
  const messageWithAdditionalHelp = additionalHelp !== undefined ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [message, " ", additionalHelp]
  }) : message;
  const InputComponent = usePropertyInputComponent({
    isHidingHighlySensitiveValue,
    isPropertyReadOnly,
    enableMultipleEmailInput,
    enablePopovers,
    enableCheckboxInput,
    enablePhoneNumberInput,
    use
  });
  const isTieredPricingProperty = useIsTieredPricingProperty({
    objectTypeId,
    property
  });
  if (!InputComponent) {
    return null;
  }
  const formControlReadOnly = !!formControlProps.readOnly || isPropertyReadOnly || disabled;
  const hideDisplayOptimizedBorderIndicator = !!formControlProps.hideDisplayOptimizedBorderIndicator || formControlReadOnly || accessLevel === HIDDEN || isFile(property) || shouldShowAIComponentForStatus(aiProcessingStatus) || isTieredPricingProperty;
  const validationError = shouldValidate && invalid;
  const error = !!formControlProps.error || validationError ? true : formControlProps.error;
  const validationMessage = formControlProps.validationMessage || (shouldValidate ? messageWithAdditionalHelp : undefined);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(PropertyInputFormControl, Object.assign({
      Component: FormControlComponent
    }, formControlProps, {
      error: error,
      validationMessage: validationMessage,
      readOnly: formControlReadOnly,
      hideDisplayOptimizedBorderIndicator: hideDisplayOptimizedBorderIndicator,
      formControlUse: use,
      onDetailsClick: onDetailsClick,
      children: /*#__PURE__*/_jsx(InputComponent, Object.assign({}, inputProps, {
        onOpenChange: wrappedOnOpenChange,
        autoFocus: autoFocus,
        disabled: disabled,
        inputMode: inputMode,
        inputRef: inputRefProp || inputRef,
        onBlur: wrappedOnBlur
        // These change handlers are provided by usePropertyValidation
        // so it can intercept onChange events and trigger validation
        ,
        onChange: onChangeSingle,
        onChangeMultiple: onChangeMultiple,
        onFocus: wrappedOnFocus,
        onKeyUp: onKeyUp,
        onTracking: onTracking,
        onValidationChange: wrappedOnValidationChange,
        readOnly: isPropertyReadOnly,
        formControlUse: use,
        showAutoSaveText: showAutoSaveTextInternal,
        enablePopovers: enablePopovers,
        popoverProps: popoverProps,
        error: error,
        validationMessage: validationMessage,
        disableValidation: disableValidation,
        popoverOpen: popoverOpen,
        setPopoverOpen: setPopoverOpen,
        htmlInputOverride: htmlInputOverride
      }))
    })), conditionalPropertiesPanel, deactivateProductModal]
  });
};
const DefaultActions = {};
const PropertyInputV2 = props => {
  const {
      accessLevel: accessLevelOverride,
      actions = DefaultActions,
      objectId,
      objectTypeId,
      property,
      value,
      formMetadata,
      enableConditionalPropertyOptions,
      onFocus = emptyFunction,
      onBlur = emptyFunction,
      formControlProps,
      // @ts-expect-error TS confused by union, its OK if this pulls undefined
      getAdditionalPropertyValue,
      // @ts-expect-error TS confused by union, its OK if this pulls undefined
      getSavedValue,
      // @ts-expect-error TS confused by union, its OK if this pulls undefined
      getDraftValue
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  useComponentMountMetric('PropertyInputV2');
  usePropertyInputCanaryMetrics(props);
  const {
    normalizedObjectTypeId,
    normalizedProperty,
    normalizedGetAdditionalPropertyValue
  } = usePropertyInputNormalization({
    objectTypeId,
    property,
    getAdditionalPropertyValue,
    getDraftValue,
    getSavedValue
  });
  const {
    data: conditionalPropertyOptionsData
  } = useConditionalPropertyOptions({
    property: normalizedProperty,
    enableConditionalPropertyOptions,
    objectTypeId: normalizedObjectTypeId,
    objectId,
    getAdditionalPropertyValue: normalizedGetAdditionalPropertyValue,
    value
  });
  const formControlPropsWithConditionalPropertyOptions = useMemo(() => {
    if (!conditionalPropertyOptionsData) {
      return formControlProps;
    }

    // We want to show any validation errors from the conditional property options.
    // But, if the host app passed us an error, we defer to that instead.
    const {
      error,
      validationMessage
    } = conditionalPropertyOptionsData;
    if (formControlProps && formControlProps.error || !error) {
      return formControlProps;
    }
    return Object.assign({}, formControlProps, {
      error,
      validationMessage
    });
  }, [conditionalPropertyOptionsData, formControlProps]);
  const actionsContextValue = useMemo(() => ({
    actions
  }), [actions]);
  return /*#__PURE__*/_jsx(PropertyContextProvider, {
    accessLevelOverride: accessLevelOverride,
    objectTypeId: normalizedObjectTypeId,
    objectId: objectId,
    value: value,
    property: conditionalPropertyOptionsData ? conditionalPropertyOptionsData.property : normalizedProperty,
    autoManageHighlySensitiveValue: false,
    getAdditionalPropertyValue: normalizedGetAdditionalPropertyValue,
    children: /*#__PURE__*/_jsx(IdentifierProvider, {
      formMetadata: formMetadata,
      children: /*#__PURE__*/_jsx(ActionsContext.Provider, {
        value: actionsContextValue,
        children: /*#__PURE__*/_jsx(FocusProvider, {
          onFocus: onFocus,
          onBlur: onBlur,
          children: /*#__PURE__*/_jsx(PropertyInputV2Wrapped, Object.assign({}, rest, {
            formControlProps: formControlPropsWithConditionalPropertyOptions
          }))
        })
      })
    })
  });
};
export default PropertyInputV2;